import { useEffect, useRef, useState } from 'react';
import { api, type Visitor, type VisitorDetail } from '../api.js';
import { faNum } from '../labels.js';

const SESSION_FA: Record<string, string> = {
  active: 'در حال گفتگو با ربات',
  awaiting_operator: 'منتظر اپراتور',
  with_operator: 'با اپراتور',
};

function ago(iso: string): string {
  const secs = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 1000));
  if (secs < 60) return 'همین حالا';
  const mins = Math.round(secs / 60);
  if (mins < 60) return `${faNum(String(mins))} دقیقه پیش`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${faNum(String(hours))} ساعت پیش`;
  return new Date(iso).toLocaleDateString('fa-IR');
}

/** A visitor who has not identified themselves still needs a sayable name. */
function label(v: { displayName: string | null; visitorNumber: number }): string {
  return v.displayName?.trim() || `بازدیدکننده ${faNum(String(v.visitorNumber))}`;
}

function prettyPath(url: string | null): string {
  if (!url) return '—';
  try {
    return decodeURIComponent(new URL(url).pathname) || '/';
  } catch {
    return url;
  }
}

export function Visitors({ inboxId }: { inboxId?: string | undefined }) {
  const [rows, setRows] = useState<Visitor[]>([]);
  const [selected, setSelected] = useState<string | null>(null);
  const [detail, setDetail] = useState<VisitorDetail | null>(null);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const selectedRef = useRef<string | null>(null);
  selectedRef.current = selected;

  async function load() {
    const res = await api.visitors(inboxId ? { inboxId } : {});
    setRows(res.visitors);
    setLoaded(true);
    const id = selectedRef.current;
    if (id) setDetail(await api.visitor(id));
  }

  useEffect(() => {
    void load();
    // Presence goes stale quickly; this is the whole point of the view.
    const tick = setInterval(() => void load(), 10_000);
    return () => clearInterval(tick);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inboxId]);

  async function open(id: string) {
    setSelected(id);
    setSent(false);
    setDetail(await api.visitor(id));
  }

  async function send() {
    if (!selected || !draft.trim()) return;
    setSending(true);
    try {
      await api.messageVisitor(selected, draft.trim());
      setDraft('');
      setSent(true);
      await load();
    } finally {
      setSending(false);
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <h2 className="text-lg font-bold">بازدیدکنندگان آنلاین</h2>
        <span className="badge bg-ok-bg text-ok-deep">
          {faNum(String(rows.length))} نفر
        </span>
        <span className="text-xs text-ink/40">هر ۱۰ ثانیه به‌روز می‌شود</span>
      </div>

      <div className="grid gap-4 lg:grid-cols-[1fr_340px]">
        <div className="card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-paper text-right text-xs text-ink/50">
                <tr>
                  <th className="p-3 font-medium">بازدیدکننده</th>
                  <th className="p-3 font-medium">صفحه‌ی فعلی</th>
                  <th className="p-3 font-medium">وضعیت</th>
                  <th className="p-3 font-medium">آخرین فعالیت</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-ink/[.07]">
                {!loaded && (
                  <tr><td colSpan={4} className="p-8 text-center text-ink/40">در حال بارگذاری…</td></tr>
                )}
                {loaded && rows.length === 0 && (
                  <tr>
                    <td colSpan={4} className="p-10 text-center text-ink/40">
                      الان کسی روی سایت نیست.
                      <div className="mt-1 text-xs">
                        بازدیدکننده وقتی اینجا ظاهر می‌شود که ویجت روی صفحه بارگذاری شده باشد.
                      </div>
                    </td>
                  </tr>
                )}
                {rows.map((v) => (
                  <tr
                    key={v.id}
                    onClick={() => void open(v.id)}
                    className={`cursor-pointer ${selected === v.id ? 'bg-brand-row' : 'hover:bg-paper'}`}
                  >
                    <td className="p-3">
                      <div className="flex items-center gap-2">
                        <span className="h-2 w-2 shrink-0 rounded-full bg-ok" />
                        <div>
                          <div className="font-medium">{label(v)}</div>
                          {v.wpUserId ? (
                            <div className="text-xs text-ink/40">
                              کاربر عضو
                              {!v.identityVerified && (
                                <span className="ms-1 text-warn-text">· تأییدنشده</span>
                              )}
                            </div>
                          ) : (
                            <div className="text-xs text-ink/40">مهمان</div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="max-w-xs p-3">
                      <div className="line-clamp-1 text-ink/65">
                        {v.currentTitle ?? prettyPath(v.currentUrl)}
                      </div>
                      <div className="line-clamp-1 text-xs text-ink/40" dir="ltr">
                        {prettyPath(v.currentUrl)}
                      </div>
                    </td>
                    <td className="p-3 whitespace-nowrap">
                      {v.sessionStatus ? (
                        <span className="badge bg-brand-100 text-brand">
                          {SESSION_FA[v.sessionStatus] ?? v.sessionStatus}
                        </span>
                      ) : (
                        <span className="text-ink/25">گفتگویی نکرده</span>
                      )}
                    </td>
                    <td className="p-3 whitespace-nowrap text-ink/50">
                      {ago(v.lastSeenAt)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="card max-h-[70vh] space-y-4 overflow-y-auto p-4 text-sm">
          {!detail && <p className="text-ink/40">یک بازدیدکننده را انتخاب کن</p>}
          {detail && (
            <>
              <section>
                <h3 className="font-bold">{label(detail.visitor)}</h3>
                <dl className="mt-2 space-y-1 text-ink/65">
                  {detail.visitor.email && (
                    <div className="flex justify-between gap-2">
                      <dt className="text-ink/40">ایمیل</dt>
                      <dd dir="ltr" className="truncate">{detail.visitor.email}</dd>
                    </div>
                  )}
                  {detail.visitor.wpUserId && (
                    <div className="flex justify-between gap-2">
                      <dt className="text-ink/40">شناسه وردپرس</dt>
                      <dd dir="ltr">{detail.visitor.wpUserId}</dd>
                    </div>
                  )}
                  <div className="flex justify-between gap-2">
                    <dt className="text-ink/40">اولین بازدید</dt>
                    <dd>{ago(detail.visitor.createdAt)}</dd>
                  </div>
                </dl>
                {detail.visitor.wpUserId && !detail.visitor.identityVerified && (
                  <p className="mt-2 rounded-lg bg-warn-bg px-3 py-2 text-xs text-warn-deep">
                    این هویت را خودِ صفحه اعلام کرده و تأیید نشده است. تا نصب افزونه
                    وردپرس، برای کارهای حساس مثل سفارش به آن اتکا نکن.
                  </p>
                )}
              </section>

              <section>
                <h4 className="mb-2 font-bold">شروع گفتگو</h4>
                {detail.sessions[0]?.status === 'active' && (
                  <p className="mb-2 rounded-lg bg-brand-100 px-3 py-2 text-xs text-brand">
                    این کاربر همین حالا با ربات در حال گفتگوست. با فرستادن پیام،
                    گفتگو به تو منتقل می‌شود.
                  </p>
                )}
                <textarea
                  className="input min-h-[70px]"
                  placeholder="سلام! کمکی از من برمیاد؟"
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                />
                <button
                  className="btn-primary mt-2 w-full"
                  disabled={!draft.trim() || sending}
                  onClick={() => void send()}
                >
                  {sending ? 'در حال ارسال…' : 'ارسال پیام'}
                </button>
                {sent && (
                  <p className="mt-2 text-xs text-ok">
                    پیام فرستاده شد. در تب گفتگوها ادامه بده.
                  </p>
                )}
              </section>

              <section>
                <h4 className="mb-2 font-bold">
                  صفحات دیده‌شده ({faNum(String(detail.pages.length))})
                </h4>
                <ul className="space-y-1.5">
                  {detail.pages.length === 0 && (
                    <li className="text-ink/40">هنوز صفحه‌ای ثبت نشده</li>
                  )}
                  {detail.pages.map((p, i) => (
                    <li key={i}>
                      <a
                        href={p.url}
                        target="_blank"
                        rel="noreferrer"
                        className="line-clamp-1 text-ink/65 hover:text-brand"
                      >
                        {p.productName ?? p.title ?? prettyPath(p.url)}
                      </a>
                      <span className="text-xs text-ink/40">{ago(p.viewedAt)}</span>
                    </li>
                  ))}
                </ul>
              </section>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
